'use client';
import { Close } from '@mui/icons-material';
import { Box, Button, Fade, IconButton, Modal, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { mergeAttributes, Node } from '@tiptap/core';
import { type NodeViewProps, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { Fragment, type MouseEvent, type ReactElement, useState } from 'react';
import Latex from 'react-latex-next/dist';
import { useCourses } from '@/providers';
import { isExercise } from '@/types';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    source: {
      setSource: (attrs: {
        pages?: string[];
        chapters?: string[];
        paragraphs?: string[];
      }) => ReturnType;
    };
  }
}

function SourceComponent(props: NodeViewProps) {
  const { node } = props;
  const theme = useTheme();
  const { selectedChapter } = useCourses();
  const { pages = [], chapters = [], paragraphs = [] } = node.attrs || {};
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedParagraph, setSelectedParagraph] = useState<{
    index: number;
    text: string;
  } | null>(null);

  // Check if we're in result mode and have articles
  const isInResultMode =
    selectedChapter && isExercise(selectedChapter) && selectedChapter.status === 'submitted';
  const hasArticles =
    selectedChapter &&
    isExercise(selectedChapter) &&
    selectedChapter.articles &&
    selectedChapter.articles.length > 0;
  const shouldShowInteractive = isInResultMode && hasArticles;

  const handleParagraphClick = (event: MouseEvent<HTMLElement>, paragraphIndex: string) => {
    if (!shouldShowInteractive) return;

    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);

    if (selectedChapter && isExercise(selectedChapter) && selectedChapter.articles) {
      const [article] = selectedChapter.articles;
      const paragraph = article.paragraphs.find(
        (item) => item.index === Number.parseInt(paragraphIndex, 10),
      );
      if (paragraph) {
        setSelectedParagraph({ index: paragraph.index, text: paragraph.text });
      }
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedParagraph(null);
  };

  const open = Boolean(anchorEl);

  const parts: (string | ReactElement)[] = [];
  if (pages.length > 0) parts.push(`Seiten: ${pages.join(', ')}`);
  if (chapters.length > 0) parts.push(`Kapitel: ${chapters.join(', ')}`);

  // Handle paragraphs with interactive behavior
  if (paragraphs.length > 0) {
    if (shouldShowInteractive) {
      const paragraphElements = paragraphs.map((p: string, index: number) => (
        <Button
          key={p}
          variant="outlined"
          size="small"
          onClick={(e) => handleParagraphClick(e, p)}
          sx={{
            fontSize: '0.75em',
            fontWeight: 'inherit',
            lineHeight: 1,
            minWidth: 'auto',
            padding: '3px',
            margin: '0 1px',
            color: 'text.primary',
            borderColor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
            textDecoration: 'none',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'action.hover',
              borderColor: 'text.secondary',
            },
          }}>
          {index > 0 && ', '}
          {p}
        </Button>
      ));
      parts.push(
        <Box component="span" key="paragraphs">
          Abschnitte: {paragraphElements}
        </Box>,
      );
    } else {
      parts.push(`Abschnitte: ${paragraphs.join(', ')}`);
    }
  }

  return (
    <>
      <NodeViewWrapper as="span" data-type="source">
        <Box
          component="span"
          sx={{
            color: theme.palette.text.secondary,
            background: theme.palette.action.hover,
            borderRadius: 1,
            p: 0.5,
            mx: 0.5,
            fontWeight: 500,
          }}>
          {parts.map((part, index) => (
            <Fragment key={typeof part === 'string' ? part : 'paragraphs-box'}>
              {index > 0 && ' | '}
              {part}
            </Fragment>
          ))}
        </Box>
      </NodeViewWrapper>

      {/* Modal for paragraph content */}
      <Modal
        open={open}
        onClose={handleClose}
        closeAfterTransition
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 1,
        }}>
        <Fade in={open}>
          <Paper
            sx={{
              maxWidth: { xs: 'calc(100vw - 16px)', sm: 640 },
              width: { xs: 'calc(100vw - 16px)', sm: 'auto' },
              maxHeight: {
                xs: 'calc(100vh - 120px)',
                sm: 'calc(100vh - 100px)',
              },
              overflowY: 'auto',
              p: 2,
              borderRadius: 2,
              boxShadow: theme.shadows[8],
              outline: 'none',
            }}>
            {selectedParagraph && (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Abschnitt {selectedParagraph.index}
                  </Typography>
                  <IconButton onClick={handleClose} size="small" sx={{ ml: 1 }}>
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
                <Latex>{selectedParagraph.text}</Latex>
              </Box>
            )}
          </Paper>
        </Fade>
      </Modal>
    </>
  );
}

export const Source = Node.create<{ HTMLAttributes: Record<string, unknown> }>({
  name: 'source',
  group: 'inline',
  inline: true,
  selectable: true,
  atom: true,

  addAttributes() {
    return {
      pages: {
        default: [],
        parseHTML: (element: HTMLElement): string[] => {
          const val = element.getAttribute('data-pages');
          return val ? (JSON.parse(val) as string[]) : [];
        },
        renderHTML: (attributes: Record<string, unknown>): Record<string, string> => {
          const pages = attributes.pages as string[] | undefined;
          if (!pages || pages.length === 0) return {};
          return { 'data-pages': JSON.stringify(pages) };
        },
      },
      chapters: {
        default: [],
        parseHTML: (element: HTMLElement): string[] => {
          const val = element.getAttribute('data-chapters');
          return val ? (JSON.parse(val) as string[]) : [];
        },
        renderHTML: (attributes: Record<string, unknown>): Record<string, string> => {
          const chapters = attributes.chapters as string[] | undefined;
          if (!chapters || chapters.length === 0) return {};
          return { 'data-chapters': JSON.stringify(chapters) };
        },
      },
      paragraphs: {
        default: [],
        parseHTML: (element: HTMLElement): string[] => {
          const val = element.getAttribute('data-paragraphs');
          return val ? (JSON.parse(val) as string[]) : [];
        },
        renderHTML: (attributes: Record<string, unknown>): Record<string, string> => {
          const paragraphs = attributes.paragraphs as string[] | undefined;
          if (!paragraphs || paragraphs.length === 0) return {};
          return { 'data-paragraphs': JSON.stringify(paragraphs) };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="source"]',
      },
    ];
  },

  renderHTML({
    HTMLAttributes,
  }: {
    HTMLAttributes: Record<string, unknown>;
  }): [string, Record<string, unknown>, string] {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'source',
        style: 'background: #eef; padding: 2px 4px; border-radius: 3px;',
      }) as Record<string, unknown>,
      'Source',
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(SourceComponent);
  },
});
